import * as React from 'react'
import { Dropdown, ConfigProvider } from 'antd'
import type { MenuProps } from 'antd'
import { cn } from '@/utils/cn'

/*
 * DropdownMenu — antd Dropdown with backward-compat compound API.
 *
 * Usage:
 * <DropdownMenu>
 * <DropdownMenuTrigger asChild>
 * <Button variant="ghost" size="icon"><MoreOutlined /></Button>
 * </DropdownMenuTrigger>
 * <DropdownMenuContent align="end">
 * <DropdownMenuItem onClick={handler}>Cap nhat</DropdownMenuItem>
 * <DropdownMenuSeparator />
 * <DropdownMenuItem className="text-destructive">Xoa</DropdownMenuItem>
 * </DropdownMenuContent>
 * </DropdownMenu>
 */

interface DropdownMenuContextValue {
  triggerRef: React.RefObject<HTMLElement | null>
}

const DropdownMenuContext = React.createContext<DropdownMenuContextValue>({
  triggerRef: { current: null },
})

function extractMenuItems(children: React.ReactNode): MenuProps['items'] {
  let keyIdx = 0
  const items: MenuProps['items'] = []
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return
    const el = child as React.ReactElement<{
      children?: React.ReactNode
      onClick?: () => void
      className?: string
      disabled?: boolean
    }>
    const dn = (el.type as { displayName?: string }).displayName
    if (dn === 'DropdownMenuSeparator') {
      items.push({ type: 'divider', key: `div-${keyIdx++}` })
    } else if (dn === 'DropdownMenuItem') {
      const isDanger =
        typeof el.props.className === 'string' &&
        el.props.className.includes('destructive')
      items.push({
        key: `item-${keyIdx++}`,
        label: el.props.children,
        danger: isDanger,
        disabled: el.props.disabled,
        className: cn(
          el.props.className,
          !isDanger && 'hover:!bg-[rgba(206,122,88,0.1)] hover:!text-[#CE7A58]'
        ),
        onClick: el.props.onClick
          ? ({ domEvent }) => {
            domEvent.stopPropagation()
            el.props.onClick?.()
          }
          : undefined,
      })
    }
  })
  return items
}

const DropdownMenu = ({ children }: { children?: React.ReactNode }) => {
  const triggerRef = React.useRef<HTMLElement | null>(null)
  return (
    <DropdownMenuContext.Provider value={{ triggerRef }}>
      <DropdownMenuRoot>{children}</DropdownMenuRoot>
    </DropdownMenuContext.Provider>
  )
}
DropdownMenu.displayName = 'DropdownMenu'

function DropdownMenuRoot({ children }: { children?: React.ReactNode }) {
  const childrenArray = React.Children.toArray(children)
  const triggerNode =
    childrenArray.find(
      (c): c is React.ReactElement =>
        React.isValidElement(c) &&
        (c.type as { displayName?: string }).displayName ===
        'DropdownMenuTrigger',
    ) ?? null
  const contentNode =
    childrenArray.find(
      (c): c is React.ReactElement =>
        React.isValidElement(c) &&
        (c.type as { displayName?: string }).displayName ===
        'DropdownMenuContent',
    ) ?? null

  const contentEl = contentNode as React.ReactElement<{
    children?: React.ReactNode
    align?: 'start' | 'center' | 'end'
    side?: 'top' | 'right' | 'bottom' | 'left'
  }> | null

  const menuItems = contentEl ? extractMenuItems(contentEl.props.children) : []

  if (!menuItems || menuItems.length === 0) {
    return null
  }

  let antdPlacement: 'bottomLeft' | 'bottom' | 'bottomRight' | 'topLeft' | 'top' | 'topRight' = 'bottomLeft'

  if (contentEl?.props.align === 'end') {
    antdPlacement = 'bottomRight'
  } else if (contentEl?.props.align === 'center') {
    antdPlacement = 'bottom'
  }

  return (
    <ConfigProvider
      theme={{
        components: {
          Dropdown: {
            colorBgElevated: '#ffffff',
          },
          Menu: {
            colorBgElevated: '#ffffff',
            itemBg: '#ffffff',
          },
        },
      }}
    >
      <Dropdown menu={{ items: menuItems }} trigger={['click']} placement={antdPlacement}>
        <span className="inline-flex">
          {triggerNode
            ? (
              triggerNode as React.ReactElement<{
                asChild?: boolean
                children?: React.ReactNode
              }>
            ).props.children
            : null}
        </span>
      </Dropdown>
    </ConfigProvider>
  )
}

const DropdownMenuTrigger = ({
  children,
  asChild: _a,
}: {
  children?: React.ReactNode
  asChild?: boolean
}) => <>{children}</>
DropdownMenuTrigger.displayName = 'DropdownMenuTrigger'

const DropdownMenuContent = ({
  children,
  align: _a,
  side: _side,
  sideOffset: _s,
  className: _c,
}: {
  children?: React.ReactNode
  align?: 'start' | 'center' | 'end'
  side?: 'top' | 'right' | 'bottom' | 'left'
  sideOffset?: number
  className?: string
}) => <>{children}</>
DropdownMenuContent.displayName = 'DropdownMenuContent'

const DropdownMenuItem = ({
  children,
  className: _c,
  onClick: _o,
  inset: _i,
  disabled: _d,
}: {
  children?: React.ReactNode
  className?: string
  onClick?: () => void
  inset?: boolean
  disabled?: boolean
}) => <>{children}</>
DropdownMenuItem.displayName = 'DropdownMenuItem'

const DropdownMenuSeparator = ({ className: _c }: { className?: string }) =>
  null
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator'

const DropdownMenuLabel = ({
  children,
  className,
  inset: _i,
}: {
  children?: React.ReactNode
  className?: string
  inset?: boolean
}) => (
  <div
    className={cn(
      'px-3 py-1.5 text-xs font-semibold text-muted-foreground',
      className,
    )}
  >
    {children}
  </div>
)
DropdownMenuLabel.displayName = 'DropdownMenuLabel'

// Stubs for less-used parts
const DropdownMenuGroup = ({ children }: { children?: React.ReactNode }) => (
  <>{children}</>
)
DropdownMenuGroup.displayName = 'DropdownMenuGroup'

const DropdownMenuPortal = ({ children }: { children?: React.ReactNode }) => (
  <>{children}</>
)
DropdownMenuPortal.displayName = 'DropdownMenuPortal'

const DropdownMenuSub = ({ children }: { children?: React.ReactNode }) => (
  <>{children}</>
)
DropdownMenuSub.displayName = 'DropdownMenuSub'

const DropdownMenuSubTrigger = ({
  children,
  className,
}: {
  children?: React.ReactNode
  className?: string
  inset?: boolean
}) => <div className={className}>{children}</div>
DropdownMenuSubTrigger.displayName = 'DropdownMenuSubTrigger'

const DropdownMenuSubContent = ({
  children,
  className,
}: {
  children?: React.ReactNode
  className?: string
}) => <div className={className}>{children}</div>
DropdownMenuSubContent.displayName = 'DropdownMenuSubContent'

const DropdownMenuRadioGroup = ({
  children,
}: {
  children?: React.ReactNode
  value?: string
  onValueChange?: (v: string) => void
}) => <>{children}</>
DropdownMenuRadioGroup.displayName = 'DropdownMenuRadioGroup'

const DropdownMenuCheckboxItem = ({
  children,
  className,
  checked: _c,
  onCheckedChange: _o,
}: {
  children?: React.ReactNode
  className?: string
  checked?: boolean
  onCheckedChange?: (v: boolean) => void
}) => <div className={className}>{children}</div>
DropdownMenuCheckboxItem.displayName = 'DropdownMenuCheckboxItem'

const DropdownMenuRadioItem = ({
  children,
  className,
  value: _v,
}: {
  children?: React.ReactNode
  className?: string
  value?: string
}) => <div className={className}>{children}</div>
DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem'

const DropdownMenuShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn(
      'ml-auto text-xs tracking-widest text-muted-foreground',
      className,
    )}
    {...props}
  />
)
DropdownMenuShortcut.displayName = 'DropdownMenuShortcut'

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuGroup,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioItem,
  DropdownMenuShortcut,
}
